/**
 * Filter Persistence Mixin (Global)
 *
 * Registered as a global mixin in main.js — auto-activates for any component
 * that declares a `persistFilters` option. No import needed.
 *
 * Saves and restores declared filter data properties + scroll positions
 * to/from sessionStorage so that navigating away and returning preserves
 * the user's exact view state.
 *
 * Usage:
 *   export default {
 *     persistFilters: {
 *       keys: ["search", "selectedStatus", "currentPage", "pageSize", "sortColumn", "sortDir"],
 *       scrollPersistence: true,  // opt-in: saves/restores window + table scroll positions
 *       // Optional: server-side LiveSearchSelect filters needing label cache
 *       optionKeys: {
 *         selectedPartyId: "partyOptions",  // filterKey -> optionsArrayName
 *       },
 *     },
 *     methods: {
 *       // loadData/loadReport/loadStatement are auto-wrapped — no manual guard needed
 *       async loadData() { ... },
 *       clearFilters() {
 *         this.clearSavedFilters();  // <-- add this call
 *         // ... existing reset code
 *       },
 *     },
 *   };
 */

const QUERY_TO_FILTER_MAP = {
  status: ["selectedStatus", "statusFilter"],
  type: ["selectedType"],
  category: ["activeCategory"],
  orderCategory: ["orderCategory"],
};

export default {
  created() {
    const config = this.$options.persistFilters;
    if (!config?.keys?.length) return;

    this._filterPersistenceActive = true;
    this._isRestoring = false;
    this._scrollPersistenceActive = !!config.scrollPersistence;
    this._scrollRestored = false;

    // When the user arrives via a deliberate drilldown — either a
    // QUERY_TO_FILTER_MAP key (e.g. ?status=Rejected) OR the explicit
    // ?fresh=1 sentinel that dashboards append even on un-filtered links
    // — treat it as a fresh entry: discard any persisted filters for
    // this page and use only the route query values. Otherwise the
    // previously-saved search / status / page number would override
    // the dashboard's intent.
    const hasRouteOverride =
      this.$route.query?.fresh !== undefined ||
      this._getRouteQueryKeys(config.keys).size > 0;

    if (hasRouteOverride) {
      sessionStorage.removeItem(this._filterStorageKey());
      if (this._scrollPersistenceActive) {
        sessionStorage.removeItem(this._scrollStorageKey());
      }
      // Strip the ?fresh=1 sentinel from the URL so a reload or back-button
      // doesn't keep re-triggering the reset. Real filter keys are left
      // intact (they're consumed by _applyRouteQueryFilters).
      if (this.$route.query.fresh !== undefined) {
        const { fresh, ...rest } = this.$route.query;
        void fresh;
        this.$router.replace({ path: this.$route.path, query: rest });
      }
    } else {
      const raw = sessionStorage.getItem(this._filterStorageKey());
      if (raw) {
        this._isRestoring = true;
        this._restoreFilters(raw);
        this._wrapLoadMethods();
      }
    }

    this._applyRouteQueryFilters(config.keys);
    this._setupFilterWatchers();
  },

  mounted() {
    if (!this._filterPersistenceActive) return;

    if (this._isRestoring) {
      // Clear any debounced search timers that started during restoration
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = null;
      }
      this._isRestoring = false;
    }

    // Set up scroll restoration watcher
    if (this._scrollPersistenceActive) {
      this._unwatchLoading = this.$watch("loading", (newVal, oldVal) => {
        if (oldVal === true && newVal === false && !this._scrollRestored) {
          this._restoreScrollPositions();
        }
      });

      // Fallback: if loading is already false (manual-search views, or data loaded before watcher)
      if (this.loading === false) {
        this.$nextTick(() => {
          if (!this._scrollRestored) {
            this._restoreScrollPositions();
          }
        });
      }
    }
  },

  // NOTE: activated() refresh is owned by the global keepAliveRefresh mixin
  // (see src/mixins/keepAliveRefresh.js). Adding an activated() hook here
  // would double-fire loadData on every cache re-activation.

  beforeUnmount() {
    if (!this._filterPersistenceActive) return;

    if (this._scrollPersistenceActive) {
      this._saveScrollPositions();
      if (this._unwatchLoading) {
        this._unwatchLoading();
        this._unwatchLoading = null;
      }
    }
  },

  methods: {
    _filterStorageKey() {
      return `filters:${this.$route.path}`;
    },

    _scrollStorageKey() {
      return `scroll:${this.$route.path}`;
    },

    _restoreFilters(raw) {
      const config = this.$options.persistFilters;
      let saved;
      try {
        saved = JSON.parse(raw);
      } catch {
        sessionStorage.removeItem(this._filterStorageKey());
        return;
      }

      const queryOverrides = this._getRouteQueryKeys(config.keys);

      for (const key of config.keys) {
        if (queryOverrides.has(key)) continue;
        if (key in saved && saved[key] !== undefined) {
          this[key] = saved[key];
        }
      }

      // Restore cached option labels for server-side LiveSearchSelect
      if (config.optionKeys && saved._optionCache) {
        for (const [filterKey, optionsArrayName] of Object.entries(config.optionKeys)) {
          const cached = saved._optionCache[filterKey];
          if (cached && this[filterKey] && Array.isArray(this[optionsArrayName])) {
            if (!this[optionsArrayName].find((o) => o.id === cached.id)) {
              this[optionsArrayName].push(cached);
            }
          }
        }
      }
    },

    _getRouteQueryKeys(filterKeys) {
      const overrides = new Set();
      const query = this.$route.query;
      if (!query || Object.keys(query).length === 0) return overrides;

      for (const [qKey, fKeys] of Object.entries(QUERY_TO_FILTER_MAP)) {
        if (query[qKey] === undefined) continue;
        const targets = Array.isArray(fKeys) ? fKeys : [fKeys];
        for (const fKey of targets) {
          if (filterKeys.includes(fKey)) overrides.add(fKey);
        }
      }
      return overrides;
    },

    _applyRouteQueryFilters(filterKeys) {
      const query = this.$route.query;
      if (!query || Object.keys(query).length === 0) return;

      for (const [qKey, fKeys] of Object.entries(QUERY_TO_FILTER_MAP)) {
        const val = query[qKey];
        if (val === undefined) continue;
        const targets = Array.isArray(fKeys) ? fKeys : [fKeys];
        for (const fKey of targets) {
          if (filterKeys.includes(fKey)) {
            this[fKey] = String(val);
          }
        }
      }
    },

    _setupFilterWatchers() {
      const config = this.$options.persistFilters;
      for (const key of config.keys) {
        this.$watch(key, () => {
          if (!this._isRestoring) {
            this._saveFilters();
          }
        });
      }
    },

    /**
     * Wraps loadData/loadReport/loadStatement methods to auto-skip during restoration.
     * Eliminates the need for each view to add `if (this._isRestoring) return;` manually.
     */
    _wrapLoadMethods() {
      const vm = this;
      const names = ["loadData", "loadReport", "loadStatement"];
      for (const name of names) {
        if (typeof vm[name] === "function") {
          const original = vm[name];
          vm[name] = function (...args) {
            if (vm._isRestoring) return;
            return original.apply(vm, args);
          };
        }
      }
    },

    _saveFilters() {
      const config = this.$options.persistFilters;
      const blob = {};
      for (const key of config.keys) {
        blob[key] = this[key];
      }

      if (config.optionKeys) {
        blob._optionCache = {};
        for (const [filterKey, optionsArrayName] of Object.entries(config.optionKeys)) {
          const id = this[filterKey];
          if (id != null && Array.isArray(this[optionsArrayName])) {
            const opt = this[optionsArrayName].find((o) => o.id === id);
            if (opt) {
              blob._optionCache[filterKey] = { id: opt.id, text: opt.text };
            }
          }
        }
      }

      sessionStorage.setItem(this._filterStorageKey(), JSON.stringify(blob));
    },

    _saveScrollPositions() {
      if (!this._scrollPersistenceActive) return;

      const scrollData = {
        windowY: window.scrollY,
        tables: [],
      };

      if (this.$el) {
        const containers = this.$el.querySelectorAll(".table-responsive");
        containers.forEach((el, index) => {
          if (el.scrollLeft > 0 || el.scrollTop > 0) {
            scrollData.tables.push({
              index,
              scrollLeft: el.scrollLeft,
              scrollTop: el.scrollTop,
            });
          }
        });
      }

      sessionStorage.setItem(this._scrollStorageKey(), JSON.stringify(scrollData));
    },

    _restoreScrollPositions() {
      if (!this._scrollPersistenceActive || this._scrollRestored) return;
      this._scrollRestored = true;

      const raw = sessionStorage.getItem(this._scrollStorageKey());
      if (!raw) return;

      let scrollData;
      try {
        scrollData = JSON.parse(raw);
      } catch {
        sessionStorage.removeItem(this._scrollStorageKey());
        return;
      }

      // Consume after reading — one-shot restoration
      sessionStorage.removeItem(this._scrollStorageKey());

      this.$nextTick(() => {
        requestAnimationFrame(() => {
          // Restore window scroll
          if (scrollData.windowY) {
            window.scrollTo(0, scrollData.windowY);
          }

          // Restore table-responsive container scroll positions
          if (scrollData.tables?.length && this.$el) {
            const containers = this.$el.querySelectorAll(".table-responsive");
            for (const saved of scrollData.tables) {
              const el = containers[saved.index];
              if (el) {
                el.scrollLeft = saved.scrollLeft;
                el.scrollTop = saved.scrollTop;
              }
            }
          }
        });
      });
    },

    clearSavedFilters() {
      sessionStorage.removeItem(this._filterStorageKey());
      if (this._scrollPersistenceActive) {
        sessionStorage.removeItem(this._scrollStorageKey());
      }
    },
  },
};
